import React, { createContext, useContext, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { cleanupAuthState } from "@/lib/authCleanup";
import { captureAttribution, persistAttributionForCurrentUser } from "@/lib/attribution";


interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Bloque 5: captura del origen publicitario (gclid/gbraid/wbraid + UTMs)
    // en cada carga. Sin PII y sin efectos en la UI.
    try {
      captureAttribution();
    } catch {
      /* la medición jamás rompe la app */
    }

    // Listener first (prevents missing events on init)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        // Diferido para no bloquear el callback de auth de Supabase.
        setTimeout(() => { void persistAttributionForCurrentUser(); }, 0);
      }
    });

    // Then get existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });


    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      setLoading(true);
      
      // Limpiar estado de autenticación general
      cleanupAuthState();
      
      // Cerrar sesión solo en este dispositivo (evita revocar tokens de otros dispositivos)
      await supabase.auth.signOut({ scope: 'local' });
      
      // Limpiar estado local
      setSession(null);
      setUser(null);
      
    } catch (error) {
      console.warn('Error during logout:', error);
      // Forzar limpieza incluso si hay error
      cleanupAuthState();
      setSession(null);
      setUser(null);
    } finally {
      setLoading(false);
      // Redireccionar después de un breve delay para asegurar que el estado se limpie
      setTimeout(() => {
        window.location.href = '/auth';
      }, 100);
    }
  };
  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
